import React, { useMemo } from "react";
import classNames from 'classnames';
import useQueryParams from "../../hooks/useQueryParams";
import ResolvePagination from "../ResolvePagination";
import ArrowdownIcon from "../../icons/Arrowdown.icon";
import ChevrondownIcon from "../../icons/Chevrondown.icon";
import Button from "../Button";

import "./ResolveTable.styles.scss";

export type TableContentTransformations<T> = Partial<
  Record<keyof T, (val: any, config?: Record<string, any>) => React.ReactNode>
>;

type ResolveTableProps<T>={
    className?:string;
    data: Array<T>;
    columns: Array<keyof T>;
    headingTranslations?: Partial<Record<keyof T, string>>;
    tableContentTransformations?: TableContentTransformations<T>;
    sortingValues?: Partial<Record<keyof T, string>>;
    isDataLoading?: boolean;
    totalPages?: number;
}

function ResolveTable<T extends Record<string, any>>(
    props: ResolveTableProps<T>,
) {   
  const {
    className,
    columns,
    data,
    headingTranslations,
    tableContentTransformations,
    isDataLoading,
    sortingValues,
    totalPages=0,
  }=props;

  const {
    setQueryParam,
    params: { sort , page=1},
  } = useQueryParams<{ sort: string; page: string }>();

  const classes=classNames(className, "resolve-table", {
    "loading":isDataLoading
  });

  const TableHeading = useMemo(
      () => (
        <thead>
          <tr>
            {columns.map((c) => {
              const sortingVal = sortingValues?.[c];
              const isSelected =
                sortingVal && sort?.includes(sortingVal as string);
  
              return (
                <th key={c as string} className={c as string}>
                  <div
                    className={classNames({
                      selected: isSelected,
                      rotated: sort !== sortingVal,
                    })}
                  >
                    {sortingVal ? (
                      <Button
                        onClick={() => {
                          if (sort === sortingVal) {
                            setQueryParam('sort', `-${sortingVal}`);
                          } else {
                            setQueryParam('sort', sortingVal);
                          }
                          setQueryParam('page', 1);
                        }}
                        className={classNames({
                          selected: isSelected,
                          rotated: sort === sortingVal,
                        })}
                      >
                        {headingTranslations?.[c] || (c as string)}
                        {isSelected ? (
                          <ArrowdownIcon className="srt-arr" />
                        ) : (
                          <div className="arw-cnt">
                            <ChevrondownIcon /> <ChevrondownIcon />
                          </div>
                        )}
                      </Button>
                    ) : (
                      headingTranslations?.[c] || (c as string)
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
      ),
      [columns, headingTranslations, setQueryParam, sort, sortingValues],
    );

    const TableBody = useMemo(
      () => (
        <tbody>
          {data.map((inst) => (
            <tr key={inst._id}>
              {columns.map((c) => (
                <td key={c as string}>
                  {tableContentTransformations?.[c as keyof T]?.(inst[c]) ||
                    inst[c]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      ),
      [columns, data, tableContentTransformations],
    );

    return (
      <div   className={classes}>
        <table>
          {TableHeading}
          {TableBody}
        </table>
        <ResolvePagination currentPage={+page} lastPage={totalPages} setPage={(num)=>setQueryParam("page",num)} />
      </div>
    );
};

export default ResolveTable;